import React, { useState, useMemo } from 'react';
import { 
  Search, TrendingUp, DollarSign, ShoppingBag, 
  ArrowUpRight, ArrowDownRight, Filter, Download, 
  Calendar, ChevronRight, PieChart as PieChartIcon,
  X, Zap, Users, Package, AlertCircle, MoreHorizontal
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/report.css';

const SALES_STATS = [
  { label: 'Revenue', value: '$4,280.50', change: '+12.5%', trend: 'up', icon: <DollarSign size={16}/> },
  { label: 'Orders', value: '156', change: '+8.2%', trend: 'up', icon: <ShoppingBag size={16}/> },
  { label: 'Avg. Check', value: '$27.44', change: '-2.1%', trend: 'down', icon: <Users size={16}/> },
  { label: 'Conversion', value: '14.2%', change: '+0.4%', trend: 'up', icon: <Zap size={16}/> },
];

const CATEGORY_CHIPS = [
  { name: 'All', count: 156 },
  { name: 'Pizza', count: 42 },
  { name: 'Burgers', count: 38 },
  { name: 'Drinks', count: 65 },
  { name: 'Desserts', count: 11 },
];

const DAILY_SALES = [
  { id: 1, name: 'Margherita Pizza', category: 'Pizza', sold: 42, revenue: 609.00, perf: 88, margin: '65%', img: 'https://images.unsplash.com/photo-1574071318508-1cdbad80ad38?auto=format&fit=crop&w=150&q=80' },
  { id: 2, name: 'The Beast Burger', category: 'Burgers', sold: 38, revenue: 627.00, perf: 92, margin: '58%', img: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=150&q=80' },
  { id: 3, name: 'Craft IPA', category: 'Drinks', sold: 65, revenue: 487.50, perf: 75, margin: '82%', img: 'https://images.unsplash.com/photo-1532634896-26909d0d4b89?auto=format&fit=crop&w=150&q=80' },
  { id: 4, name: 'Lava Cake', category: 'Desserts', sold: 24, revenue: 216.00, perf: 64, margin: '70%', img: 'https://images.unsplash.com/photo-1624353339193-2f0391b211d1?auto=format&fit=crop&w=150&q=80' },
  { id: 5, name: 'Truffle Mushroom', category: 'Pizza', sold: 18, revenue: 324.00, perf: 45, margin: '60%', img: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=150&q=80' },
  { id: 6, name: 'Chicken Zinger', category: 'Burgers', sold: 31, revenue: 372.00, perf: 70, margin: '55%', img: 'https://images.unsplash.com/photo-1610614819513-58e34989848b?auto=format&fit=crop&w=150&q=80' },
];

function DailySalesReport() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const filteredSales = useMemo(() => {
    return DAILY_SALES.filter(s => 
      (activeTab === 'All' || s.category === activeTab) &&
      s.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, activeTab]);

  return (
    <div className="report-container">
      <Navbar />
      
      <main className={`report-layout ${selectedProduct ? 'sb-open' : ''}`}>
        <section className="report-content">
          <header className="compact-header">
            <div className="title-block">
              <div className="date-pill"><Calendar size={14}/> Jan 17, 2026</div>
              <h2>Daily Revenue <span className="accent">Summary</span></h2>
            </div>
            
            <div className="action-row">
              <div className="search-box">
                <Search size={14} />
                <input type="text" placeholder="Search report..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn-secondary"><Filter size={14}/> Filters</button>
              <button className="btn-primary"><Download size={14}/> Export</button>
            </div>
          </header>

          <div className="kpi-strip">
            {SALES_STATS.map((s, i) => (
              <div key={i} className="kpi-item">
                <div className="kpi-icon-wrap">{s.icon}</div>
                <div className="kpi-info">
                  <label>{s.label}</label>
                  <div className="kpi-row">
                    <span className="kpi-val">{s.value}</span>
                    <span className={`kpi-trend ${s.trend}`}>{s.change}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="category-filter-strip">
            {CATEGORY_CHIPS.map(c => (
              <button 
                key={c.name} 
                className={`filter-chip ${activeTab === c.name ? 'active' : ''}`}
                onClick={() => setActiveTab(c.name)}
              >
                {c.name} <span className="chip-count">{c.count}</span>
              </button>
            ))}
          </div>

          <div className="data-table-header">
            <span>Product Details</span>
            <span>Units</span>
            <span>Revenue</span>
            <span>Gross Margin</span>
            <span>Performance</span>
          </div>

          <div className="data-list">
            {filteredSales.map(item => (
              <div 
                key={item.id} 
                className={`data-row ${selectedProduct?.id === item.id ? 'selected' : ''}`}
                onClick={() => setSelectedProduct(item)}
              >
                <div className="col-product">
                  <img src={item.img} alt="" className="row-thumb" />
                  <div>
                    <div className="row-name">{item.name}</div>
                    <div className="row-sub">{item.category}</div>
                  </div>
                </div>
                <div className="col-data"><strong>{item.sold}</strong></div>
                <div className="col-data accent-text"><strong>${item.revenue.toFixed(2)}</strong></div>
                <div className="col-data text-muted">{item.margin}</div>
                <div className="col-progress">
                  <div className="progress-bg"><div className="progress-fill" style={{width: `${item.perf}%`}}></div></div>
                  <span className="perf-text">{item.perf}%</span>
                </div>
                <div className="col-action"><ChevronRight size={16}/></div>
              </div>
            ))}
          </div>
        </section>

        <aside className="report-sidebar">
          {selectedProduct ? (
            <div className="sb-inner animate-fade-in">
              <div className="sb-nav">
                <button className="btn-icon" onClick={() => setSelectedProduct(null)}><X size={18}/></button>
                <span className="sb-title">Product Analytics</span>
                <button className="btn-icon"><MoreHorizontal size={18}/></button>
              </div>

              <div className="sb-stats-card">
                <img src={selectedProduct.img} className="sb-img" alt="" />
                <div className="sb-info">
                  <h4>{selectedProduct.name}</h4>
                  <div className="sb-badges">
                    <span className="badge">High Margin</span>
                    <span className="badge">Active</span>
                  </div>
                </div>
              </div>

              <div className="sb-grid">
                <div className="sb-box">
                  <label>Est. Net Profit</label>
                  <div className="sb-val">${(selectedProduct.revenue * 0.42).toFixed(2)}</div>
                </div>
                <div className="sb-box">
                  <label>Order Velocity</label>
                  <div className="sb-val">4.2/hr</div>
                </div>
              </div>

              <div className="sb-section">
                <h5><Zap size={14} className="accent"/> Hourly Demand</h5>
                <div className="spark-line">
                  {[20, 50, 30, 80, 40, 90, 60].map((h, i) => (
                    <div key={i} className="spark-bar" style={{height: `${h}%`}}></div>
                  ))}
                </div>
              </div>

              <div className="sb-alert">
                <AlertCircle size={16}/>
                <div>
                  <div className="alert-title">Inventory Forecast</div>
                  <div className="alert-desc">Stock will deplete in 4 hours based on current velocity.</div>
                </div>
              </div>

              <button className="btn-full-primary">Adjust Stock Levels</button>
            </div>
          ) : (
            <div className="sb-empty">
              <PieChartIcon size={40} className="muted-icon"/>
              <p>Select a product to view <br/>real-time velocity & forecasting.</p>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}

export default DailySalesReport;